import React from "react";
import {styled, useTheme} from "@mui/material/styles";
import {keyframes} from "@emotion/react";

/**
 * Skote "growing" spinner (Bootstrap's `spinner-grow` look used by the Skote template): a filled
 * dot that pulses instead of a rotating ring.
 *
 * <p>Drop-in replacement for MUI's `<CircularProgress />` in loading states. It accepts the same
 * commonly used props (`size`, `color`, `sx`, `style`, `className`) plus `thickness`, which is
 * ignored so existing call sites do not have to be rewritten.</p>
 */
const grow = keyframes`
    0% {
        transform: scale(0);
        opacity: 0;
    }
    50% {
        opacity: 1;
        transform: scale(0.9);
    }
    100% {
        opacity: 0;
        transform: scale(1.1);
    }
`;

const Root = styled("span")({
    display: "inline-block",
    flex: "0 0 auto",
    borderRadius: "50%",
    verticalAlign: "middle",
    backgroundColor: "currentColor",
    animation: `${grow} 0.9s linear infinite`,
    // Respect the OS "reduce motion" setting: keep a static dot (still conveys
    // "busy" through aria-label) instead of an infinite pulse.
    "@media (prefers-reduced-motion: reduce)": {
        animation: "none",
        opacity: 0.85,
    },
});

const GrowingSpinner = (props:any) => {
    const {
        size = 32,
        color = "primary",
        sx,
        style,
        className,
        ...rest
    } = props;
    // `thickness` is a CircularProgress-only prop: drop it so it never lands on the DOM node.
    delete rest.thickness;
    const theme:any = useTheme();
    const palette:any = theme?.palette?.[color];
    const backgroundColor = color === "inherit" || color === "currentColor"
        ? "currentColor"
        : (palette?.main ?? theme?.palette?.primary?.main);

    return (
        <Root
            role="status"
            aria-label="Loading"
            className={className}
            style={{width: size, height: size, backgroundColor, ...style}}
            sx={sx}
            {...rest}
        />
    );
};

export default GrowingSpinner;
